import { useState } from 'react'
import { IconCopy } from '../icons/index.js'

/**
 * Code Field — `DESIGN-HANDOVER.md` §5.5, Figma `145:18235` (`328x48`).
 *
 * `variant`, OPT-IN (Epic 49 §8.8, added — shared-component rule: default = today's behaviour
 * byte-for-byte, matching the `PromptInput`/`NumberedElementsOverlay` opt-in-prop pattern):
 *  - `'text'` (default, UNCHANGED): field radius `8px`, button `63x32` at radius `6px`, filled
 *    `#3b45e6` with `#ffffff` "Kopyala"/"Kopyalandı" text.
 *  - `'icon'` — node `183:26213` (Import's per-master row, `150×48`, where the 63px text button
 *    does not fit the row's narrower column): the same read-only field, but the button becomes a
 *    compact `32×32` ICON-ONLY square — `surface-tertiary` (light grey) fill, `IconCopy`, no
 *    visible label — measured via `get_design_context`: field `pl-16 pr-8 py-8`, `gap-16`
 *    between the id text and the button; button `min-h-32`, `p-8`, radius `6px` (`rounded-150`).
 *    An icon-only control with no accessible name is unusable by a screen reader (hard rule 1),
 *    so `ariaLabel` is REQUIRED in practice — it falls back to `copyLabel` only so the control is
 *    never nameless if a caller forgets to pass a field-specific one.
 *  - `'text-icon'` — node `156:21470`, the Import empty state (`156:21015`), where the component
 *    is drawn at its own measured `145:18235` shape: field `pl-16 pr-8 py-8`, `gap-16`, the id in
 *    Body/Default/MEDIUM (not regular); button filled `#3b45e6`, `min-h-32`, `p-8`, `gap-4`, label
 *    + a 12px `IconCopy` after it. Added 2026-09-14 rather than restyling `'text'`, whose three
 *    callers (Create Campaign, the plugin lightbox, Import's left panel) are accepted as they are.
 *
 * The copied confirmation is TEXT for `'text'` (or an accessible-name swap for `'icon'`), never a
 * colour flip alone — §6.1's rule that a state is never carried by colour alone applies to
 * transient feedback too.
 */
export function CodeField({
  value,
  copyLabel = 'Kopyala',
  copiedLabel = 'Kopyalandı',
  variant = 'text',
  ariaLabel,
  className,
}: {
  value: string
  /** User-facing copy. Turkish by default. */
  copyLabel?: string
  copiedLabel?: string
  /** `'text'` (default, unchanged 63×32 filled button), `'icon'` (opt-in 32×32 icon-only
   * button) or `'text-icon'` (opt-in label + icon button) — see the file header for where each
   * is used. */
  variant?: 'text' | 'icon' | 'text-icon'
  /** `'icon'` variant only: accessible name for the icon-only button (e.g. `"300×250 kimliğini
   * kopyala"`). Falls back to `copyLabel` when omitted. Ignored by the `'text'` variant, which
   * already carries its own visible label. */
  ariaLabel?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  const copy = () => {
    // `clipboard` is absent in jsdom and on insecure origins; the label must not lie.
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }

  if (variant === 'icon') {
    return (
      <div
        className={`flex h-12 items-center gap-xlarge rounded-200 border border-border-primary bg-surface-white py-medium pr-medium pl-xlarge${className ? ` ${className}` : ''}`}
      >
        <code className="min-w-0 flex-1 truncate text-body-default-regular text-text-default">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? copiedLabel : (ariaLabel ?? copyLabel)}
          className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-150 bg-surface-tertiary text-icon-default hover:bg-surface-tertiary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1"
        >
          <IconCopy size={16} />
        </button>
      </div>
    )
  }

  if (variant === 'text-icon') {
    return (
      <div
        className={`flex h-12 items-center gap-xlarge rounded-200 border border-border-primary bg-surface-white py-medium pr-medium pl-xlarge${className ? ` ${className}` : ''}`}
      >
        <code className="min-w-0 flex-1 truncate font-sans text-body-default-medium text-text-default">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex min-h-8 shrink-0 cursor-pointer items-center justify-center gap-small rounded-150 bg-surface-primary p-medium text-body-default-medium text-text-primary-on-color hover:bg-surface-primary-hover active:bg-surface-primary-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1"
        >
          {copied ? copiedLabel : copyLabel}
          <IconCopy size={12} aria-hidden="true" />
        </button>
      </div>
    )
  }

  return (
    <div
      className={`flex h-12 items-center justify-between gap-medium rounded-200 border border-border-primary bg-surface-white px-large${className ? ` ${className}` : ''}`}
    >
      <code className="truncate text-body-default-regular text-text-default">{value}</code>
      <button
        type="button"
        onClick={copy}
        className="inline-flex h-8 w-[63px] shrink-0 cursor-pointer items-center justify-center rounded-150 bg-surface-primary text-body-default-medium text-text-primary-on-color hover:bg-surface-primary-hover active:bg-surface-primary-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1"
      >
        {copied ? copiedLabel : copyLabel}
      </button>
    </div>
  )
}
